import axios from "axios";
import { API_URL, authHeader } from "@/api/api-quatro";

const REPORT_ENDPOINT_PATH = "/doc-mdbooking-failed-api";

const RECENT_ERRORS = new Map();
const DEDUPE_WINDOW_MS = 10000;
const MAX_FIELD_LENGTH = 8000;

// Sensitive-key handling. All key comparisons are case-insensitive.
//
// We split into three buckets so a support ticket can identify *which*
// doctor / patient / record failed without leaking actual PII:
//
//   1. REDACT_KEYS  – replaced with "[REDACTED]". Free-text PII (names,
//                     address, email, DOB) and any auth secrets / heavy
//                     nested payloads.
//   2. PHONE_KEYS   – partial mask: keeps the carrier prefix and the last
//                     2 digits, e.g. 0501234567 -> "050*****67".
//   3. ID_KEYS      – partial mask: keeps only the last 3 digits of an
//                     Israeli ID / passport, e.g. 123456789 -> "******789".

const REDACT_KEYS = new Set([
  // Auth / secrets
  "password",
  "token",
  "two_factor_token",
  "authorization",
  "google_subscription_token",
  // Patient identity (free-text)
  "first_name",
  "firstname",
  "last_name",
  "lastname",
  "patient_name",
  "patientname",
  "address",
  "email",
  "birth_date",
  "birthdate",
  "apotropos",
  // Heavy / nested payloads that typically contain PII or medical content
  "data_json",
  "files",
  "base64"
]);

const PHONE_KEYS = new Set([
  "phone",
  "patient_phone",
  "patientphone",
  "second_phone",
  "phone2"
]);

const ID_KEYS = new Set([
  "passport",
  "patientpassport",
  "patient_passport",
  "id_num",
  "id_number"
]);

/**
 * Send a structured error report to the backend.
 * Never throws — telemetry must never break the user flow.
 *
 * @param {object} payload
 */
export async function reportClientApiFailure(payload) {
  if (!payload) return;
  try {
    await axios.post(`${API_URL}${REPORT_ENDPOINT_PATH}`, payload, {
      headers: {
        Authorization: authHeader(),
        "Content-Type": "application/json"
      }
    });
  } catch {
    // Intentionally ignore.
  }
}

function shouldSkipReport(error) {
  if (!error) return true;
  if (axios.isCancel && axios.isCancel(error)) return true;
  const status = error.response && error.response.status;
  if (status === 401) return true;
  const url = (error.config && error.config.url) || "";
  if (url.indexOf(REPORT_ENDPOINT_PATH) !== -1) return true;
  return false;
}

/**
 * Read the active doctor's license from the Vuex store's localStorage
 * stash so the report can be traced back to a specific doctor without
 * digging through cookies on the backend. License is the stable
 * identifier used across the API.
 */
function getReporterDoctor() {
  const nullObject = { license: null, name: null };
  try {
    if (typeof localStorage === "undefined") return nullObject;
    const raw = localStorage.getItem("activeDoctor");
    if (!raw) return nullObject;
    const doc = JSON.parse(raw) || {};
    return { license: doc.license || null, name: doc.name || null };
  } catch (_) {
    return nullObject;
  }
}

/**
 * Pick a single legacy-shape identifier ("surgery_123" / "appointment_456")
 * for the existing key_id column on the backend. Other identifiers carried
 * by the request are already preserved inside `request` / `url`.
 */
function extractKeyId(cfg) {
  const data = cfg && cfg.data;
  if (!data || typeof data !== "object") return null;
  if (data.surgery_id) return `surgery_${data.surgery_id}`;
  const appId = data.appointment_id || data.appoitment_id;
  if (appId) return `appointment_${appId}`;
  return null;
}

/**
 * Non-sensitive upload metadata for support when an upload request fails.
 * Does not include file contents or base64.
 *
 * @param {import("axios").AxiosRequestConfig} cfg
 * @returns {object | null}
 */
function extractUploadAttemptSummary(cfg) {
  const url = (cfg && cfg.url) || "";
  const fullUrl = ((cfg && cfg.baseURL) || "") + url;
  let data = cfg && cfg.data;

  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const files = [];
    try {
      for (const [field, value] of data.entries()) {
        if (typeof File !== "undefined" && value instanceof File) {
          files.push({
            field,
            client_filename: value.name || null,
            mime_type: value.type || null,
            size_bytes: typeof value.size === "number" ? value.size : null
          });
        } else if (
          typeof Blob !== "undefined" &&
          value instanceof Blob &&
          !(typeof File !== "undefined" && value instanceof File)
        ) {
          files.push({
            field,
            client_filename: null,
            mime_type: value.type || null,
            size_bytes: typeof value.size === "number" ? value.size : null
          });
        }
      }
    } catch (_) {
      return { source: "multipart", file_entries: [], note: "iterate_failed" };
    }
    return files.length ? { source: "multipart", file_entries: files } : null;
  }

  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (_) {
      return null;
    }
  }

  if (
    url.indexOf("upload_external_files") !== -1 &&
    data &&
    typeof data === "object" &&
    typeof data.data_json === "string"
  ) {
    try {
      const parsed = JSON.parse(data.data_json);
      const name = parsed.name != null ? String(parsed.name) : null;
      let file_extension = null;
      if (name) {
        const dot = name.lastIndexOf(".");
        if (dot >= 0 && dot < name.length - 1) {
          file_extension = name.slice(dot + 1).toLowerCase();
        }
      }
      return {
        source: "upload_external_files",
        url: fullUrl,
        document_category: parsed.type || null,
        client_filename: name,
        file_extension
      };
    } catch (_) {
      return {
        source: "upload_external_files",
        url: fullUrl,
        parse_error: true
      };
    }
  }

  return null;
}

function buildDedupeKey(error) {
  const cfg = error.config || {};
  const url = cfg.url || "unknown";
  const method = (cfg.method || "GET").toUpperCase();
  const status =
    (error.response && error.response.status) || error.code || "ERR";
  return `${method} ${url} ${status}`;
}

function truncate(str) {
  if (typeof str !== "string") return str;
  return str.length > MAX_FIELD_LENGTH
    ? str.slice(0, MAX_FIELD_LENGTH) + "…(truncated)"
    : str;
}

/**
 * Replace all but a few digits with "*". Returns "[REDACTED]" if the value
 * is too short to be safely partially masked.
 */
function maskDigits(value, keepStart, keepEnd) {
  if (value == null) return value;
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "[REDACTED]";
  if (digits.length <= keepStart + keepEnd) return "[REDACTED]";
  const start = digits.slice(0, keepStart);
  const end = digits.slice(-keepEnd);
  return `${start}${"*".repeat(digits.length - keepStart - keepEnd)}${end}`;
}

// 0501234567 -> "050*****67". Carrier prefix is preserved (not PII), and
// the last two digits help the doctor recognise the patient in support.
const maskPhone = value => maskDigits(value, 3, 2);

// 123456789 -> "******789". Only the last 3 digits are kept.
const maskNationalId = value => maskDigits(value, 0, 3);

function sanitize(data, seen) {
  if (data == null) return data;
  if (typeof data === "string") return truncate(data);
  if (typeof data !== "object") return data;

  // Pass through objects that are not plain (FormData, Blob, File, etc.)
  // — we don't want to crash trying to clone them, and we don't want their
  // raw contents in the report anyway.
  if (
    (typeof FormData !== "undefined" && data instanceof FormData) ||
    (typeof Blob !== "undefined" && data instanceof Blob) ||
    (typeof File !== "undefined" && data instanceof File)
  ) {
    return `[${data.constructor && data.constructor.name}]`;
  }

  const tracker = seen || new WeakSet();
  if (tracker.has(data)) return "[CIRCULAR]";
  tracker.add(data);

  try {
    if (Array.isArray(data)) {
      return data.map(item => sanitize(item, tracker));
    }
    const clone = {};
    for (const key of Object.keys(data)) {
      const k = key.toLowerCase();
      const value = data[key];
      if (REDACT_KEYS.has(k)) {
        clone[key] = "[REDACTED]";
      } else if (PHONE_KEYS.has(k)) {
        clone[key] = maskPhone(value);
      } else if (ID_KEYS.has(k)) {
        clone[key] = maskNationalId(value);
      } else {
        clone[key] = sanitize(value, tracker);
      }
    }
    return clone;
  } catch (_) {
    return "[unsanitizable]";
  }
}

/**
 * Fire-and-forget API error reporter. Safe to call from axios interceptors.
 * Never throws, never blocks, never recurses on its own endpoint.
 *
 * Payload shape:
 *   {
 *     timestamp:    string,        // local Israel time
 *     page:         string,        // window.location.pathname
 *     license:      string | null, // doctor license, from local store
 *     doctor_name:  string | null, // doctor name, from local store
 *     method:       string,
 *     url:          string,        // full URL incl. baseURL
 *     status:       number | null,
 *     error:        string | null, // axios error code, e.g. "ERR_NETWORK"
 *     message:      string,
 *     key_id:       string | null, // headline id (surgery_X / appointment_X)
 *     request:      any,           // sanitized
 *     response:     any,           // sanitized
 *     upload_attempt?: object      // file type / name metadata for failed uploads (no content)
 *     context?:     any            // sanitized
 *   }
 *
 * @param {*} error - axios error object
 * @param {object} [context] - optional extra context (free-form)
 */
export function reportApiError(error, context) {
  try {
    if (shouldSkipReport(error)) return;

    const key = buildDedupeKey(error);
    const now = Date.now();
    const last = RECENT_ERRORS.get(key) || 0;
    if (now - last < DEDUPE_WINDOW_MS) return;
    RECENT_ERRORS.set(key, now);

    const cfg = error.config || {};
    const res = error.response;
    const doctor = getReporterDoctor();
    const payload = {
      timestamp: new Date().toLocaleString("he-IL", {
        timeZone: "Asia/Jerusalem",
        hour12: false
      }),
      page: typeof window !== "undefined" ? window.location.pathname : "n/a",
      license: doctor.license,
      doctor_name: doctor.name,
      method: (cfg.method || "GET").toUpperCase(),
      url: (cfg.baseURL || "") + (cfg.url || ""),
      status: res ? res.status : null,
      error: (error && (error.code || error.name)) || null,
      message: (error && error.message) || "unknown",
      key_id: extractKeyId(cfg),
      request: sanitize(cfg.data),
      response: sanitize(res && res.data)
    };
    const uploadSummary = extractUploadAttemptSummary(cfg);
    if (uploadSummary) {
      payload.upload_attempt = sanitize(uploadSummary);
    }
    if (context && Object.keys(context).length) {
      payload.context = sanitize(context);
    }

    if (process.env.NODE_ENV === "production") {
      void reportClientApiFailure(payload);
    }
  } catch (_) {
    // Telemetry must never break the user flow.
  }
}

/**
 * Attach a fire-and-forget error-reporting response interceptor to an axios
 * instance. The interceptor never alters the original error flow: it only
 * forwards the rejected error after attempting to send a telemetry report.
 *
 * Safe to call once per axios instance.
 *
 * @param {import("axios").AxiosInstance} axiosInstance
 */
export function attachApiErrorReporting(axiosInstance) {
  if (!axiosInstance || !axiosInstance.interceptors) return;

  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      try {
        reportApiError(error);
      } catch (_) {
        // telemetry must never affect the original error flow
      }
      return Promise.reject(error);
    }
  );
}
